/**
 * LocalDataService.ts - F021 Fase 2
 * 
 * Servicio de geocodificación offline usando datos DERA pre-descargados.
 * Primera línea de búsqueda antes de llamar APIs externas.
 * 
 * DATOS LOCALES (11,282 features total):
 * - health.geojson:     1,700 centros (CAP + hospitales)
 * - security.geojson:   1,259 (policía, bomberos, Guardia Civil)
 * - education.geojson:  6,725 centros educativos
 * - municipal.geojson:  1,414 (ayuntamientos + centros Junta)
 * - emergency.geojson:     23 centros gestión emergencias
 * - energy.geojson:       161 parques eólicos
 * 
 * CARACTERÍSTICAS:
 * - Singleton con lazy loading
 * - Índice por cod_mun para O(1) lookup
 * - Fuzzy matching con uFuzzy para nombres (10-100x más rápido que Fuse.js)
 * - Coordenadas ya en EPSG:25830 (UTM30 ETRS89)
 * 
 * @version 1.1.0
 * @date 2025-12-03
 */

import { FastFuzzy } from './fuzzySearch';
import { quitarPrefijoEducativo, elegirCentroEducativo, tipoContradice, esSoloPrefijoEducativo, elegirUnicoDelTipo } from './siglasEducativas';
import Flatbush from 'flatbush';
import { InfrastructureType } from '../types/infrastructure';
import { db, SPATIAL_INDEX_ID, type SpatialIndexData } from './localData/schemas';
import { getR2DataService, type R2Dataset } from '../services/data';

// ============================================================================
// TIPOS
// ============================================================================

export type InfrastructureCategory = 
  | 'health' 
  | 'security' 
  | 'education' 
  | 'municipal' 
  | 'emergency'
  | 'energy'
  | 'hydraulic-rediam'   // RESIL-WFS-S3: volcado REDIAM (EDAR/ETAP/DEPOSITO/ELEVADORAS/POTABILIZADORA/COLECTOR)
  | 'hydraulic-cdav';     // GEO-CDAV: volcado CDAV (EMBALSE+PRESA, ~1.200 features)

export interface LocalFeature {
  /** Index signature para compatibilidad con FastFuzzy<T extends Record<string, unknown>> */
  [key: string]: string | number | undefined;
  id: string;
  nombre: string;
  tipo?: string;
  direccion?: string;
  localidad?: string;
  codMun: string;
  municipio: string;
  provincia: string;
  x: number;
  y: number;
  categoria: InfrastructureCategory;
  /**
   * `PREFEDU-b2`: tipo de centro que da DERA (`origen.tipo` de R2: «Escuela Infantil», «Colegio de Educación
   * Infantil y Primaria»…). DERA quitó el tipo del nombre de 1 474 centros («San Agustín»), y `tipo` guarda el
   * subtipo de la capa («CENTRO_EDUCATIVO»). Solo lo usa la guarda educativa para desempatar homónimos.
   */
  tipoDera?: string;
}

export interface LocalSearchQuery {
  /** Nombre de la infraestructura a buscar */
  nombre: string;
  /** Código INE del municipio (5 dígitos) */
  codMun?: string;
  /** Nombre del municipio (alternativa a codMun) */
  municipio?: string;
  /** Provincia para desambiguación */
  provincia?: string;
  /** Categorías donde buscar (default: todas) */
  categorias?: InfrastructureCategory[];
  /** Límite de resultados (default: 5) */
  maxResults?: number;
  /** Umbral mínimo de similitud Fuse.js (default: 0.4) */
  threshold?: number;
}

export interface LocalSearchResult {
  success: boolean;
  features: LocalFeature[];
  bestMatch: LocalFeature | null;
  matchScore: number;
  source: 'LOCAL_DERA';
  searchTime: number;
  totalSearched: number;
  fromCache: boolean;
  /** `PREFEDU`: el centro salió del reintento sin prefijo educativo (nota fija `NOTA_REINTENTO_EDUCATIVO`). */
  reintentoSinPrefijo?: boolean;
  /** `NOTAPOS`: el 2.º candidato casa con nota casi igual (homónimos); la nota no pasa de `NOTA_TOPE_EMPATE` (84). */
  empateNombre?: boolean;
}

/**
 * `PREFEDU` 29-oct-2026 · reintento sin prefijo educativo (`DOP-PREFEDU-2`=a, `-3`, `-3b`=a).
 * La Junta renombró en DERA 4 087 centros («C.E.I.P. El Pinar» → «El Pinar» o «Colegio de
 * Educación Infantil y Primaria El Pinar»). Si el nombre del PTEL no casa y la búsqueda es solo
 * de `education`, se busca sin prefijo y elige `elegirCentroEducativo` (guarda de nombre y tipo).
 * Nota 84: pasa el corte de la oleada 1 (70) sin confirmarse sola (85). La elección la hace la guarda
 * de nombre y tipo, no el buscador difuso, así que la nota de parecido (`NOTAPOS`) no se usa aquí.
 */
const NOTA_REINTENTO_EDUCATIVO = 84;

const sinTildesMinusculas = (s: string) => s.normalize('NFD').replace(/[\u0300-\u036f]/g, '').toLowerCase();

/**
 * `PREFEDU-b2` DOP-2 (a) · nombre del centro con su tipo de DERA delante cuando DERA se lo quitó
 * («San Agustín» + «Escuela Infantil» → «Escuela Infantil San Agustín»); si el nombre ya empieza por su
 * tipo, o no hay tipo, el nombre tal cual. Solo para la guarda educativa (desempate de homónimos).
 */
export function nombreConTipoDera(f: Pick<LocalFeature, 'nombre' | 'tipoDera'>): string {
  const t = f.tipoDera;
  if (!t) return f.nombre;
  const primera = sinTildesMinusculas(t).split(' ')[0];
  return sinTildesMinusculas(f.nombre).startsWith(primera) ? f.nombre : `${t} ${f.nombre}`;
}

// ============================================================================
// CONFIGURACIÓN
// ============================================================================

// Base URL de Vite (incluye trailing slash)
const BASE_URL = import.meta.env.BASE_URL || '/';

const DATA_PATHS: Record<InfrastructureCategory, string> = {
  health: `${BASE_URL}data/dera/health.geojson`,
  security: `${BASE_URL}data/dera/security.geojson`,
  education: `${BASE_URL}data/dera/education.geojson`,
  municipal: `${BASE_URL}data/dera/municipal.geojson`,
  emergency: `${BASE_URL}data/dera/emergency.geojson`,
  energy: `${BASE_URL}data/dera/energy.geojson`,
  // RESIL-WFS-S3: R2 global, no hay fichero local estático
  'hydraulic-rediam': '',
  // GEO-CDAV: R2 global, no hay fichero local estático
  'hydraulic-cdav': '',
};

/** Mapeo de categorías a R2Dataset */
const CATEGORY_TO_R2: Record<InfrastructureCategory, R2Dataset | null> = {
  health: 'dera/health',
  security: 'dera/security',
  education: 'dera/education',
  municipal: 'dera/municipal',
  emergency: 'dera/emergency',
  energy: 'dera/energy',
  // RESIL-WFS-S3: REDIAMHydraulicR2Service usa su propia ruta Worker; no pasa por R2DataService
  'hydraulic-rediam': null,
  // GEO-CDAV: CDAVHydraulicR2Service usa su propia ruta Worker; no pasa por R2DataService
  'hydraulic-cdav': null,
};

// ============================================================================
// LDHM: el vacío prometido deja de ser mudo
// ============================================================================

const LDHM = '[LocalDataService]';

/**
 * LDHM: ¿tiene la categoría un conjunto R2 asignado?
 *
 * Se deriva de `CATEGORY_TO_R2`, no se duplica: el día que una categoría
 * reciba conjunto, deja de considerarse desatendida sin tocar esta función.
 */
export function hasLocalDataset(category: InfrastructureCategory): boolean {
  return CATEGORY_TO_R2[category] !== null;
}

/**
 * LDHM: avisa de las categorías que `TYPOLOGY_TO_CATEGORY` promete pero
 * `CATEGORY_TO_R2` no puede servir.
 *
 * Corrige una asimetría del módulo: una tipología mapeada a `null` en
 * `TYPOLOGY_TO_CATEGORY` (CULTURAL, DEPORTIVO…) sale por `console.debug`
 * declarándose sin datos locales; una mapeada a categoría **con** entrada
 * pero **sin** conjunto R2 (las dos hidráulicas) salía a cero features
 * **sin registro de ninguna clase**. Prometer resultaba más barato que
 * declararse nulo, y solo lo segundo era auditable.
 *
 * @returns las categorías desatendidas, para que el llamante decida.
 */
export function warnCategoriesWithoutDataset(
  categories: InfrastructureCategory[],
  origen: string,
  detalle?: string,
): InfrastructureCategory[] {
  const desatendidas = categories.filter(cat => !hasLocalDataset(cat));
  if (desatendidas.length > 0) {
    console.warn(
      `${LDHM} ${origen}: ${desatendidas.join(', ')} sin conjunto R2 asignado ` +
      `⇒ 0 features${detalle ? ` (${detalle})` : ''}. El mapa de tipologías encamina ` +
      `aquí, pero CATEGORY_TO_R2 las mapea a null: no hay datos que cargar.`,
    );
  }
  return desatendidas;
}

/**
 * Mapeo de InfrastructureType (clasificador) → InfrastructureCategory (datos locales)
 * 
 * NOTA: Los valores del enum (SANITARIO, EDUCATIVO, etc.) ya están incluidos
 * mediante [InfrastructureType.X]. Solo añadimos alias adicionales en inglés.
 */
export const TYPOLOGY_TO_CATEGORY: Record<string, InfrastructureCategory | InfrastructureCategory[] | null> = {
  // Categorías con datos locales disponibles
  [InfrastructureType.HEALTH]: 'health',
  'HEALTH': 'health',  // alias inglés
  
  [InfrastructureType.EDUCATION]: 'education',
  'EDUCATION': 'education',  // alias inglés
  
  [InfrastructureType.POLICE]: 'security',
  [InfrastructureType.FIRE]: 'security',
  'POLICE': 'security',  // alias inglés
  'FIRE': 'security',    // alias inglés
  'SECURITY': 'security',
  
  [InfrastructureType.MUNICIPAL]: 'municipal',
  'ADMIN': 'municipal',  // alias adicional
  
  [InfrastructureType.EMERGENCY]: 'emergency',
  'EMERGENCY': 'emergency',  // alias inglés
  
  [InfrastructureType.ENERGY]: 'energy',
  'ENERGY': 'energy',  // alias inglés
  
  // Categorías sin datos locales (requieren APIs externas)
  [InfrastructureType.CULTURAL]: null,
  [InfrastructureType.RELIGIOUS]: null,
  [InfrastructureType.SPORTS]: null,
  [InfrastructureType.SOCIAL]: null,
  [InfrastructureType.FUEL]: null,
  // RESIL-WFS-S3: infraestructuras hidráulicas → REDIAMHydraulicR2Service
  [InfrastructureType.HYDRAULIC]: 'hydraulic-rediam',
  'HYDRAULIC': 'hydraulic-rediam',
  'EDAR': 'hydraulic-rediam',
  'ETAP': 'hydraulic-rediam',
  'DEPOSITO': 'hydraulic-rediam',
  // GEO-CDAV: EMBALSE y PRESA → CDAV (fuente canónica Junta de Andalucía)
  'EMBALSE': 'hydraulic-cdav',
  'PRESA': 'hydraulic-cdav',
  'POTABILIZADORA': 'hydraulic-rediam',
  'ELEVADORA_AR': 'hydraulic-rediam',
  'ELEVADORA_AP': 'hydraulic-rediam',
  'COLECTOR': 'hydraulic-rediam',
  [InfrastructureType.TELECOM]: null,
  [InfrastructureType.VIAL]: null,
  [InfrastructureType.INDUSTRIAL]: null,
  [InfrastructureType.GENERIC]: null,
  // BUG-UI-07: 'OTROS' es el fallback del InfrastructureClassifier (string literal,
  // no clave del enum). Sin esta entrada, typeToCategories() devuelve [] y TSF
  // emite NO_LOCAL_DATA con warning en lugar de degradar silenciosamente a oleada 2.
  'OTROS': null,
};

/** Opciones para FastFuzzy (uFuzzy) */
const FUZZY_OPTIONS = {
  keys: ['nombre', 'direccion', 'localidad', 'tipo'] as const,
  threshold: 0.4,
};

/** F6.03: Todas las categorías por defecto */
const ALL_CATEGORIES: InfrastructureCategory[] = ['health', 'security', 'education', 'municipal', 'energy'];

// ============================================================================
// SINGLETON & ESTADO
// ============================================================================

const dataStore = new Map<InfrastructureCategory, LocalFeature[]>();
const municipioIndex = new Map<string, Map<InfrastructureCategory, LocalFeature[]>>();
const fastFuzzyInstances = new Map<InfrastructureCategory, FastFuzzy<LocalFeature>>();
let spatialIndex: Flatbush | null = null;
let spatialFeatures: LocalFeature[] = [];
let isLoaded = false;
let isLoading = false;
let loadError: Error | null = null;

// F6.03: Lazy loading por categoría
const loadedCategories = new Set<InfrastructureCategory>();
const categoryLoadPromises = new Map<InfrastructureCategory, Promise<void>>();
let spatialIndexDirty = true;

interface LoadStats {
  totalFeatures: number;
  byCategory: Record<InfrastructureCategory, number>;
  loadTimeMs: number;
  municipiosIndexados: number;
}
let loadStats: LoadStats | null = null;

// ============================================================================
// UTILIDADES
// ============================================================================

function normalizeText(text: string): string {
  return text.toLowerCase().normalize('NFD').replace(/[\u0300-\u036f]/g, '').replace(/[^a-z0-9\s]/g, ' ').replace(/\s+/g, ' ').trim();
}

function normalizeCodMun(cod: string): string {
  return cod.replace(/\D/g, '').padStart(5, '0');
}

function extractCoordinates(geometry: unknown): { x: number; y: number } | null {
  if (!geometry || typeof geometry !== 'object') return null;
  const geom = geometry as { type?: string; coordinates?: unknown };
  if (!geom.coordinates) return null;
  let coords: number[];
  if (geom.type === 'MultiPoint' && Array.isArray(geom.coordinates)) {
    coords = geom.coordinates[0] as number[];
  } else if (geom.type === 'Point' && Array.isArray(geom.coordinates)) {
    coords = geom.coordinates as number[];
  } else if (Array.isArray(geom.coordinates)) {
    const first = geom.coordinates[0];
    coords = Array.isArray(first) ? first as number[] : geom.coordinates as number[];
  } else {
    return null;
  }
  if (coords.length >= 2 && typeof coords[0] === 'number') {
    return { x: coords[0], y: coords[1] };
  }
  return null;
}

// ============================================================================
// TIPOS R2/DEXIE
// ============================================================================

/** Feature en formato R2/Dexie (ya aplanado) */
interface R2Feature {
  id: string;
  tipologia: string;
  nombre: string;
  subtipo?: string | null;
  direccion?: string;
  localidad?: string;
  codMun: string;
  municipio: string;
  provincia: string;
  codProv: string;
  x: number;
  y: number;
  capaOrigen?: string;
  metadata?: Record<string, unknown>;
  fechaCarga?: string;
}

/** Colección de features R2/Dexie */
interface R2FeatureCollection {
  type: 'DERAFeatureCollection';
  features: R2Feature[];
}

/** Mapeo de tipología R2 → InfrastructureCategory */
const _TIPOLOGIA_TO_CATEGORY: Record<string, InfrastructureCategory> = {
  'SANITARIO': 'health',
  'SEGURIDAD': 'security',
  'EDUCATIVO': 'education',
  'MUNICIPAL': 'municipal',
  'EMERGENCIA': 'emergency',
  'ENERGIA': 'energy',
};

/**
 * Convierte feature R2/Dexie a LocalFeature
 */
function r2FeatureToLocal(f: R2Feature, categoria: InfrastructureCategory): LocalFeature {
  return {
    id: f.id,
    nombre: f.nombre || 'Sin nombre',
    tipo: f.subtipo || f.capaOrigen,
    direccion: f.direccion,
    localidad: f.localidad,
    codMun: normalizeCodMun(f.codMun),
    municipio: f.municipio || '',
    provincia: f.provincia || '',
    x: f.x,
    y: f.y,
    categoria,
  };
}

/**
 * Parsea colección R2/Dexie a array de LocalFeature.
 * Acepta dos formatos:
 *   - Antiguo: { type, features: R2Feature[] }  (codMun, codProv camelCase)
 *   - Nuevo:   DERAR2Feature[]                  (cod_ine, cod_prov snake_case)
 */
function parseR2Collection(
  data: R2FeatureCollection | unknown,
  categoria: InfrastructureCategory,
): LocalFeature[] {
  // Formato nuevo: array plano DERAR2Feature[]
  if (Array.isArray(data)) {
    return (data as Array<Record<string, unknown>>)
      .filter(f => f['x'] && f['y'] && (f['cod_ine'] || f['codMun']))
      .map(f => {
        // Normalizar snake_case → camelCase para reutilizar r2FeatureToLocal
        const normalized: R2Feature = {
          id: String(f['id'] ?? ''),
          tipologia: String(f['tipologia'] ?? ''),
          nombre: String(f['nombre'] ?? ''),
          subtipo: f['subtipo'] != null ? String(f['subtipo']) : undefined,
          direccion: f['direccion'] != null ? String(f['direccion']) : undefined,
          codMun: String(f['cod_ine'] ?? f['codMun'] ?? ''),
          municipio: String(f['municipio'] ?? ''),
          provincia: String(f['provincia'] ?? ''),
          codProv: String(f['cod_prov'] ?? f['codProv'] ?? ''),
          x: Number(f['x']),
          y: Number(f['y']),
        };
        const local = r2FeatureToLocal(normalized, categoria);
        // `PREFEDU-b2` (HX-CAPTURA-MAXIMAL): se conserva el tipo de DERA para desempatar homónimos.
        const origen = f['origen'] as Record<string, unknown> | undefined;
        const tipoDera = origen?.['tipo'] != null ? String(origen['tipo']).trim() : '';
        if (tipoDera && tipoDera !== 'Sin dato') local.tipoDera = tipoDera;
        return local;
      });
  }
  // Formato antiguo: { features: R2Feature[] }
  const col = data as R2FeatureCollection;
  if (!col.features || !Array.isArray(col.features)) return [];
  return col.features
    .filter(f => f.x && f.y && f.codMun)
    .map(f => r2FeatureToLocal(f, categoria));
}

// ============================================================================
// PARSEO DE GEOJSON
// ============================================================================

interface GeoJSONFeature {
  type: string;
  id?: string;
  geometry: unknown;
  properties: Record<string, unknown>;
}

interface GeoJSONCollection {
  type: string;
  features: GeoJSONFeature[];
}

function parseGeoJSON(data: GeoJSONCollection, categoria: InfrastructureCategory): LocalFeature[] {
  const features: LocalFeature[] = [];
  for (const feature of data.features) {
    const props = feature.properties || {};
    const coords = extractCoordinates(feature.geometry);
    if (!coords) continue;
    const codMun = String(props.cod_mun || props.codigo_ine || props.ine || '');
    if (!codMun) continue;
    const localFeature: LocalFeature = {
      id: String(feature.id || props.id_dera || `${categoria}-${features.length}`),
      nombre: String(props.nombre || props.denominaci || props.name || 'Sin nombre'),
      tipo: props.tipo ? String(props.tipo) : undefined,
      // `PREFEDU-b3` (HX-CAPTURA-MAXIMAL): el tipo de DERA también en la carga de respaldo, como en `parseR2Collection`;
      // sin él, con R2 caído, el desempate de homónimos de `PREFEDU-b2` no actúa.
      ...(props.tipo && String(props.tipo).trim() !== 'Sin dato' ? { tipoDera: String(props.tipo).trim() } : {}),
      direccion: props.direccion ? String(props.direccion) : undefined,
      localidad: props.localidad ? String(props.localidad) : undefined,
      codMun: normalizeCodMun(codMun),
      municipio: String(props.municipio || props.localidad || ''),
      provincia: String(props.provincia || ''),
      x: coords.x,
      y: coords.y,
      categoria,
    };
    features.push(localFeature);
  }
  return features;
}

// ============================================================================
// CARGA DE DATOS
// ============================================================================

/** Estado para tracking de fuente de datos */
let lastDataSource: 'r2' | 'cache' | 'static' | 'geojson' | null = null;

export function getLastDataSource(): typeof lastDataSource {
  return lastDataSource;
}

export async function loadLocalData(): Promise<LoadStats> {
  if (isLoaded && loadStats) return loadStats;
  if (isLoading) {
    return new Promise((resolve, reject) => {
      const checkInterval = setInterval(() => {
        if (isLoaded && loadStats) { clearInterval(checkInterval); resolve(loadStats); }
        else if (loadError) { clearInterval(checkInterval); reject(loadError); }
      }, 100);
    });
  }
  isLoading = true;
  const startTime = performance.now();
  try {
    const categories: InfrastructureCategory[] = ['health', 'security', 'education', 'municipal', 'energy'];
    const stats: LoadStats = { totalFeatures: 0, byCategory: {} as Record<InfrastructureCategory, number>, loadTimeMs: 0, municipiosIndexados: 0 };
    
    // F004: Obtener servicio R2 con circuit breaker y caché
    const r2Service = getR2DataService();
    
    const loadPromises = categories.map(async (cat) => {
      const r2Dataset = CATEGORY_TO_R2[cat];
      if (!r2Dataset) return { cat, features: [] };
      
      // Estrategia: R2 primero (con circuit breaker), luego GeoJSON fallback
      try {
        // Intentar R2/Dexie primero (incluye caché IndexedDB y fallback a static bundle)
        const result = await r2Service.fetchDataset<R2FeatureCollection | unknown[]>(r2Dataset);
        const features = parseR2Collection(result.data, cat);
        
        if (features.length > 0) {
          console.log(`[LocalDataService] ${cat}: ${features.length} features desde ${result.source}${result.circuitBreakerActive ? ' (CB activo)' : ''} en ${Math.round(result.responseTime)}ms`);
          lastDataSource = result.source;
          return { cat, features };
        }
      } catch (r2Err) {
        console.warn(`[LocalDataService] R2 falló para ${cat}, probando GeoJSON:`, r2Err);
      }
      
      // Fallback final: GeoJSON estático original
      const path = DATA_PATHS[cat];
      try {
        const response = await fetch(path);
        if (!response.ok) { 
          console.warn(`[LocalDataService] No se pudo cargar ${path}: ${response.status}`); 
          return { cat, features: [] as LocalFeature[] }; 
        }
        const data = await response.json() as GeoJSONCollection;
        const features = parseGeoJSON(data, cat);
        lastDataSource = 'geojson';
        console.log(`[LocalDataService] ${cat}: ${features.length} features desde GeoJSON fallback`);
        return { cat, features };
      } catch (err) { 
        console.warn(`[LocalDataService] Error cargando ${cat}:`, err); 
        return { cat, features: [] as LocalFeature[] }; 
      }
    });
    const results = await Promise.all(loadPromises);
    for (const { cat, features } of results) {
      dataStore.set(cat, features);
      stats.byCategory[cat] = features.length;
      stats.totalFeatures += features.length;
      if (features.length > 0) {
        fastFuzzyInstances.set(cat, new FastFuzzy(features, { keys: FUZZY_OPTIONS.keys as unknown as (keyof LocalFeature)[], threshold: FUZZY_OPTIONS.threshold }));
      }
      for (const feature of features) {
        if (!municipioIndex.has(feature.codMun)) municipioIndex.set(feature.codMun, new Map());
        const munMap = municipioIndex.get(feature.codMun)!;
        if (!munMap.has(cat)) munMap.set(cat, []);
        munMap.get(cat)!.push(feature);
      }
    }
    stats.municipiosIndexados = municipioIndex.size;
    stats.loadTimeMs = Math.round(performance.now() - startTime);
    const allFeatures = Array.from(dataStore.values()).flat();
    if (allFeatures.length > 0) {
      spatialFeatures = allFeatures;
      
      // C.2: Intentar cargar índice serializado de IndexedDB
      let indexLoaded = false;
      try {
        const savedIndex = await db.spatialIndexes.get(SPATIAL_INDEX_ID);
        if (savedIndex && savedIndex.featureCount === allFeatures.length) {
          // Deserializar índice existente
          spatialIndex = Flatbush.from(savedIndex.data);
          indexLoaded = true;
          console.log(`[LocalDataService] Índice Flatbush cargado de IndexedDB: ${allFeatures.length} puntos (${Math.round(savedIndex.data.byteLength / 1024)}KB)`);
        } else if (savedIndex) {
          console.log(`[LocalDataService] Índice guardado obsoleto (${savedIndex.featureCount} vs ${allFeatures.length}), reconstruyendo...`);
        }
      } catch (dbErr) {
        console.warn('[LocalDataService] Error leyendo índice de IndexedDB:', dbErr);
      }
      
      // Si no se cargó, construir nuevo índice y guardarlo
      if (!indexLoaded) {
        const buildStart = performance.now();
        spatialIndex = new Flatbush(allFeatures.length);
        for (const f of allFeatures) spatialIndex.add(f.x, f.y, f.x, f.y);
        spatialIndex.finish();
        const buildTime = Math.round(performance.now() - buildStart);
        console.log(`[LocalDataService] Índice Flatbush construido: ${allFeatures.length} puntos en ${buildTime}ms`);
        
        // Guardar en IndexedDB para próxima carga
        try {
          const indexData: SpatialIndexData = {
            id: SPATIAL_INDEX_ID,
            data: spatialIndex.data as ArrayBuffer,  // Flatbush.data es ArrayBufferLike
            featureCount: allFeatures.length,
            numItems: allFeatures.length,
            createdAt: new Date().toISOString(),
            version: '1.0.0'
          };
          await db.spatialIndexes.put(indexData);
          console.log(`[LocalDataService] Índice Flatbush guardado en IndexedDB: ${Math.round(spatialIndex.data.byteLength / 1024)}KB`);
        } catch (saveErr) {
          console.warn('[LocalDataService] Error guardando índice en IndexedDB:', saveErr);
        }
      }
    }
    loadStats = stats;
    isLoaded = true;
    isLoading = false;
    console.log(`[LocalDataService] Cargado: ${stats.totalFeatures} features (${stats.municipiosIndexados} municipios) en ${stats.loadTimeMs}ms`);
    return stats;
  } catch (err) {
    loadError = err instanceof Error ? err : new Error(String(err));
    isLoading = false;
    throw loadError;
  }
}

export function isDataLoaded(): boolean { return isLoaded; }
export function getLoadStats(): LoadStats | null { return loadStats; }

// ============================================================================
// F6.03: LAZY LOADING POR CATEGORÍA
// ============================================================================

/**
 * Carga una categoría individual bajo demanda.
 * Deduplica llamadas concurrentes a la misma categoría.
 */
async function loadSingleCategory(category: InfrastructureCategory): Promise<LocalFeature[]> {
  const r2Service = getR2DataService();
  const r2Dataset = CATEGORY_TO_R2[category];
  if (!r2Dataset) {
    // LDHM: era el retorno vacío mudo que nombra la OBS.
    warnCategoriesWithoutDataset([category], 'loadSingleCategory');
    return [];
  }

  // Intentar R2/Dexie primero
  try {
    const result = await r2Service.fetchDataset<R2FeatureCollection | unknown[]>(r2Dataset);
    const features = parseR2Collection(result.data, category);
    if (features.length > 0) {
      lastDataSource = result.source;
      return features;
    }
  } catch (r2Err) {
    console.warn(`[F6.03] R2 falló para ${category}:`, r2Err);
  }

  // Fallback: GeoJSON estático
  const path = DATA_PATHS[category];
  try {
    const response = await fetch(path);
    if (!response.ok) {
      // LDHM: mudo aquí, pero `loadLocalData` sí avisa en su equivalente.
      console.warn(`[F6.03] No se pudo cargar ${path} para ${category}: ${response.status}`);
      return [];
    }
    const data = await response.json() as GeoJSONCollection;
    lastDataSource = 'geojson';
    return parseGeoJSON(data, category);
  } catch (err) {
    // LDHM: idem — el `catch` descartaba en silencio.
    console.warn(`[F6.03] Error cargando ${category} desde ${path}:`, err);
    return [];
  }
}

/**
 * Indexa features de una categoría en los índices locales
 */
function indexCategoryFeatures(category: InfrastructureCategory, features: LocalFeature[]): void {
  dataStore.set(category, features);
  if (features.length > 0) {
    fastFuzzyInstances.set(category, new FastFuzzy(features, {
      keys: FUZZY_OPTIONS.keys as unknown as (keyof LocalFeature)[],
      threshold: FUZZY_OPTIONS.threshold,
    }));
  }
  for (const feature of features) {
    if (!municipioIndex.has(feature.codMun)) municipioIndex.set(feature.codMun, new Map());
    const munMap = municipioIndex.get(feature.codMun)!;
    if (!munMap.has(category)) munMap.set(category, []);
    munMap.get(category)!.push(feature);
  }
  spatialIndexDirty = true;
}

/**
 * F6.03: Asegura que una categoría esté cargada (lazy loading).
 * Si ya está cargada, retorna inmediatamente.
 * Deduplica llamadas concurrentes mediante promesas compartidas.
 */
export async function ensureCategoryLoaded(category: InfrastructureCategory): Promise<void> {
  if (loadedCategories.has(category)) return;

  // Deduplicar: si ya hay una carga en curso para esta categoría, esperar
  const existing = categoryLoadPromises.get(category);
  if (existing) return existing;

  const loadPromise = (async () => {
    try {
      const features = await loadSingleCategory(category);
      indexCategoryFeatures(category, features);
      loadedCategories.add(category);
    } finally {
      categoryLoadPromises.delete(category);
    }
  })();

  categoryLoadPromises.set(category, loadPromise);
  return loadPromise;
}

/**
 * F6.03: Asegura que varias categorías estén cargadas en paralelo.
 */
export async function ensureCategoriesLoaded(categories: InfrastructureCategory[]): Promise<void> {
  const unloaded = categories.filter(c => !loadedCategories.has(c));
  if (unloaded.length === 0) return;
  await Promise.all(unloaded.map(c => ensureCategoryLoaded(c)));
}

/**
 * F6.03: Reconstruye el índice espacial Flatbush si está marcado como sucio.
 */
async function _ensureSpatialIndex(): Promise<void> {
  if (spatialIndex && !spatialIndexDirty) return;

  const allFeatures = Array.from(dataStore.values()).flat();
  if (allFeatures.length === 0) {
    spatialIndex = null;
    spatialFeatures = [];
    spatialIndexDirty = false;
    return;
  }

  spatialFeatures = allFeatures;
  spatialIndex = new Flatbush(allFeatures.length);
  for (const f of allFeatures) spatialIndex.add(f.x, f.y, f.x, f.y);
  spatialIndex.finish();
  spatialIndexDirty = false;
}

/**
 * F6.03: Obtiene las categorías cargadas actualmente.
 */
export function getLoadedCategories(): InfrastructureCategory[] {
  return Array.from(loadedCategories);
}

// ============================================================================
// BÚSQUEDA LOCAL
// ============================================================================

export async function searchLocal(query: LocalSearchQuery): Promise<LocalSearchResult> {
  const startTime = performance.now();
  // F6.03: Lazy loading - solo cargar categorías necesarias
  const targetCats = query.categorias || ALL_CATEGORIES;
  if (!isLoaded) await ensureCategoriesLoaded(targetCats);
  const { nombre, codMun, municipio, provincia, categorias = ['health', 'security', 'education', 'municipal', 'energy'], maxResults = 5, threshold = 0.4 } = query;
  const result: LocalSearchResult = { success: false, features: [], bestMatch: null, matchScore: 0, source: 'LOCAL_DERA', searchTime: 0, totalSearched: 0, fromCache: true };
  const normalizedNombre = normalizeText(nombre);
  if (!normalizedNombre) { result.searchTime = Math.round(performance.now() - startTime); return result; }
  let featuresToSearch: LocalFeature[] = [];
  if (codMun) {
    const normalizedCod = normalizeCodMun(codMun);
    const munData = municipioIndex.get(normalizedCod);
    if (munData) for (const cat of categorias) { const catFeatures = munData.get(cat); if (catFeatures) featuresToSearch.push(...catFeatures); }
  } else if (municipio) {
    const normalizedMun = normalizeText(municipio);
    for (const [_cod, munData] of municipioIndex) {
      for (const catFeatures of munData.values()) {
        if (catFeatures.length > 0) {
          const munName = normalizeText(catFeatures[0].municipio);
          if (munName.includes(normalizedMun) || normalizedMun.includes(munName)) {
            for (const cat of categorias) { const features = munData.get(cat); if (features) featuresToSearch.push(...features); }
            break;
          }
        }
      }
    }
  } else {
    for (const cat of categorias) { const features = dataStore.get(cat); if (features) featuresToSearch.push(...features); }
  }
  if (provincia && featuresToSearch.length > 0) {
    const normalizedProv = normalizeText(provincia);
    featuresToSearch = featuresToSearch.filter(f => { const featProv = normalizeText(f.provincia); return featProv.includes(normalizedProv) || normalizedProv.includes(featProv); });
  }
  result.totalSearched = featuresToSearch.length;
  if (featuresToSearch.length === 0) { result.searchTime = Math.round(performance.now() - startTime); return result; }
  const tempFuzzy = new FastFuzzy(featuresToSearch, { keys: FUZZY_OPTIONS.keys as unknown as (keyof LocalFeature)[], threshold });
  // NOTAPOS: nota de parecido y filtro por nota, no por puesto en la lista (`FastFuzzy.buscarPorParecido`).
  const fuzzyResults = tempFuzzy.buscarPorParecido(nombre, { limit: maxResults });
  if (fuzzyResults.length > 0) {
    result.success = true;
    result.features = fuzzyResults.map(r => r.item);
    result.bestMatch = fuzzyResults[0].item;
    result.matchScore = fuzzyResults[0].nota;
  }
  const soloEducacionDelMunicipio = !!codMun && categorias.length === 1 && categorias[0] === 'education';
  // PREFEDU: un centro de tipo contrario no es el buscado («E.I. El Bosque» ≠ «Sección de Educación
  // Permanente El Bosque»); se descarta y se pasa al reintento.
  if (result.success && soloEducacionDelMunicipio && result.bestMatch && tipoContradice(nombre, result.bestMatch.nombre)) {
    result.success = false; result.features = []; result.bestMatch = null; result.matchScore = 0;
  }
  // PREFEDU / `PREFEDU-b3` DOP-2 (a): un nombre que solo es el tipo («E.I.», «Colegio», «Escuela Municipal de Música»)
  // vale si en el municipio hay un único centro de ese tipo (con el tipo de DERA delante cuando DERA se lo quitó);
  // se elige ese centro, haya acertado o no el buscador.
  if (soloEducacionDelMunicipio && esSoloPrefijoEducativo(nombre)) {
    const conTipoU = featuresToSearch.map((f) => ({ f, nombre: nombreConTipoDera(f), tipoDera: f.tipoDera, x: f.x, y: f.y }));
    const unico = elegirUnicoDelTipo(nombre, conTipoU)?.f ?? null;
    if (!unico) { result.success = false; result.features = []; result.bestMatch = null; result.matchScore = 0; }
    else if (result.bestMatch !== unico) {
      result.success = true; result.features = [unico]; result.bestMatch = unico;
      result.matchScore = NOTA_REINTENTO_EDUCATIVO; result.reintentoSinPrefijo = true;
    }
  }
  if (!result.success && soloEducacionDelMunicipio) {
    const sinPrefijo = quitarPrefijoEducativo(nombre);
    if (sinPrefijo !== nombre) {
      // La guarda se aplica a todos los centros educativos del municipio, sin buscador difuso:
      // exige las palabras en el nombre, así que no depende de la posición ni de un tope de candidatos.
      // `PREFEDU-b2` DOP-2 (a): la guarda ve el nombre con su tipo de DERA cuando DERA se lo quitó, así distingue
      // homónimos en puntos distintos («E.I. San Agustín» frente a «C.E.I.P. San Agustín», El Ejido). Solo desempata.
      const conTipo = featuresToSearch.map((f) => ({ f, nombre: nombreConTipoDera(f), x: f.x, y: f.y }));
      const elegido = elegirCentroEducativo(nombre, conTipo)?.f ?? null;
      if (elegido) {
        result.success = true;
        result.features = [elegido];
        result.bestMatch = elegido;
        result.matchScore = NOTA_REINTENTO_EDUCATIVO;
        result.reintentoSinPrefijo = true;
      }
    }
  }
  // NOTAPOS: la marca de empate solo vale si queda el resultado del buscador (no tras las guardas ni el reintento).
  if (result.success && !result.reintentoSinPrefijo && fuzzyResults[0]?.empate) result.empateNombre = true;
  result.searchTime = Math.round(performance.now() - startTime);
  return result;
}

// ============================================================================
// BÚSQUEDAS ESPECÍFICAS POR CATEGORÍA
// ============================================================================

export async function searchHealthLocal(nombre: string, municipio?: string, codMun?: string): Promise<LocalSearchResult> {
  return searchLocal({ nombre, municipio, codMun, categorias: ['health'] });
}
export async function searchEducationLocal(nombre: string, municipio?: string, codMun?: string): Promise<LocalSearchResult> {
  return searchLocal({ nombre, municipio, codMun, categorias: ['education'] });
}
export async function searchSecurityLocal(nombre: string, municipio?: string, codMun?: string): Promise<LocalSearchResult> {
  return searchLocal({ nombre, municipio, codMun, categorias: ['security'] });
}
export async function searchMunicipalLocal(nombre: string, municipio?: string, codMun?: string): Promise<LocalSearchResult> {
  return searchLocal({ nombre, municipio, codMun, categorias: ['municipal'] });
}
export async function searchEnergyLocal(nombre: string, municipio?: string, codMun?: string): Promise<LocalSearchResult> {
  return searchLocal({ nombre, municipio, codMun, categorias: ['energy'] });
}

// ============================================================================
// UTILIDADES DE CONSULTA
// ============================================================================

export async function getFeaturesByMunicipio(codMun: string, categorias?: InfrastructureCategory[]): Promise<LocalFeature[]> {
  const cats = categorias || ALL_CATEGORIES;
  // LDHM: la ruta viva del wizard (TSFGeocoder L1 del cascade) entra por aquí.
  // El aviso va ANTES de la guarda `isLoaded`: tras `loadLocalData()` esa guarda
  // ya no deja pasar a `loadSingleCategory`, así que el vacío nacía aquí, mudo.
  warnCategoriesWithoutDataset(cats, 'getFeaturesByMunicipio', `municipio ${codMun}`);
  if (!isLoaded) await ensureCategoriesLoaded(cats);
  const normalizedCod = normalizeCodMun(codMun);
  const munData = municipioIndex.get(normalizedCod);
  if (!munData) return [];
  const result: LocalFeature[] = [];
  for (const cat of cats) { const features = munData.get(cat); if (features) result.push(...features); }
  return result;
}

export async function getFeatureCountByMunicipio(codMun: string): Promise<Record<InfrastructureCategory, number>> {
  if (!isLoaded) await ensureCategoriesLoaded(ALL_CATEGORIES);
  const normalizedCod = normalizeCodMun(codMun);
  const munData = municipioIndex.get(normalizedCod);
  const counts: Record<InfrastructureCategory, number> = { health: 0, security: 0, education: 0, municipal: 0, emergency: 0, energy: 0, 'hydraulic-rediam': 0, 'hydraulic-cdav': 0 };
  if (munData) for (const [cat, features] of munData) counts[cat] = features.length;
  return counts;
}

// ============================================================================
// F023 FASE 1.1 - MÉTODOS SINGLETON
// ============================================================================

const F023_1_1 = '[F023-1.1]';

function resolveTypologyToCategories(tipologia: string): InfrastructureCategory[] {
  const mapping = TYPOLOGY_TO_CATEGORY[tipologia.toUpperCase()];
  if (mapping === null || mapping === undefined) return [];
  if (Array.isArray(mapping)) return mapping;
  return [mapping];
}

export async function countByType(tipologia: string, codMunicipio: string): Promise<number> {
  const categories = resolveTypologyToCategories(tipologia);
  if (categories.length === 0) { console.debug(`${F023_1_1} Tipología '${tipologia}' sin datos locales disponibles`); return 0; }
  // LDHM: la línea de arriba avisa de la tipología honestamente nula. Ésta avisa
  // de la que promete categoría y no la puede servir — antes salía a 0 en silencio.
  warnCategoriesWithoutDataset(categories, `countByType('${tipologia}')`, `municipio ${codMunicipio}`);
  if (!isLoaded) await ensureCategoriesLoaded(categories);
  const normalizedCod = normalizeCodMun(codMunicipio);
  const munData = municipioIndex.get(normalizedCod);
  if (!munData) { console.debug(`${F023_1_1} Municipio ${normalizedCod} no encontrado en índice local`); return 0; }
  let count = 0;
  for (const cat of categories) { const features = munData.get(cat); if (features) count += features.length; }
  if (count === 1) console.debug(`${F023_1_1} SINGLETON detectado: ${tipologia} en ${normalizedCod}`);
  else if (count >= 2) console.debug(`${F023_1_1} Múltiples (${count}): ${tipologia} en ${normalizedCod} - requiere desambiguación`);
  return count;
}

export async function getUniqueByType(tipologia: string, codMunicipio: string): Promise<LocalFeature | null> {
  if (!isLoaded) await loadLocalData();
  const normalizedCod = normalizeCodMun(codMunicipio);
  const categories = resolveTypologyToCategories(tipologia);
  if (categories.length === 0) { console.debug(`${F023_1_1} getUnique: Tipología '${tipologia}' sin categoría local`); return null; }
  // LDHM: misma asimetría que en countByType.
  warnCategoriesWithoutDataset(categories, `getUniqueByType('${tipologia}')`, `municipio ${codMunicipio}`);
  const munData = municipioIndex.get(normalizedCod);
  if (!munData) { console.debug(`${F023_1_1} getUnique: Municipio ${normalizedCod} sin datos`); return null; }
  const allFeatures: LocalFeature[] = [];
  for (const cat of categories) { const features = munData.get(cat); if (features) allFeatures.push(...features); }
  if (allFeatures.length === 1) {
    console.debug(`${F023_1_1} ✅ Match directo singleton: '${allFeatures[0].nombre}' (${tipologia}, ${normalizedCod}) - 95% confianza`);
    return allFeatures[0];
  }
  if (allFeatures.length === 0) console.debug(`${F023_1_1} getUnique: 0 features para ${tipologia} en ${normalizedCod}`);
  else console.debug(`${F023_1_1} getUnique: ${allFeatures.length} features para ${tipologia} en ${normalizedCod} - singleton no aplicable`);
  return null;
}

export async function listMunicipiosConDatos(): Promise<string[]> {
  if (!isLoaded) await loadLocalData();
  return Array.from(municipioIndex.keys()).sort();
}

// ============================================================================
// INTEGRACIÓN CON GEOCODING CASCADE
// ============================================================================

export function toGeocodingResult(feature: LocalFeature, matchScore: number): { x: number; y: number; confidence: number; source: string; matchedName: string; crs: 'EPSG:25830' } {
  return { x: feature.x, y: feature.y, confidence: matchScore / 100, source: `LOCAL_DERA_${feature.categoria.toUpperCase()}`, matchedName: feature.nombre, crs: 'EPSG:25830' };
}

export async function geocodeWithLocalFallback<T>(nombre: string, categorias: InfrastructureCategory[], municipio?: string, codMun?: string, fallbackFn?: () => Promise<T>): Promise<{ x: number; y: number; source: string; confidence: number } | T | null> {
  const localResult = await searchLocal({ nombre, codMun, municipio, categorias, threshold: 0.35 });
  if (localResult.success && localResult.bestMatch && localResult.matchScore >= 60) return toGeocodingResult(localResult.bestMatch, localResult.matchScore);
  if (fallbackFn) {
    try { return await fallbackFn(); }
    catch { if (localResult.bestMatch && localResult.matchScore >= 40) return toGeocodingResult(localResult.bestMatch, localResult.matchScore); }
  }
  if (localResult.bestMatch) return toGeocodingResult(localResult.bestMatch, localResult.matchScore);
  return null;
}

// ============================================================================
// LIMPIEZA Y TESTING
// ============================================================================

export function clearLocalData(): void {
  dataStore.clear();
  municipioIndex.clear();
  fastFuzzyInstances.clear();
  spatialIndex = null;
  spatialFeatures = [];
  isLoaded = false;
  isLoading = false;
  loadError = null;
  loadStats = null;
}

/**
 * C.2: Invalida el índice Flatbush serializado en IndexedDB
 * Útil cuando los datos DERA se actualizan
 */
export async function clearSpatialIndexCache(): Promise<void> {
  try {
    await db.spatialIndexes.delete(SPATIAL_INDEX_ID);
    console.log('[LocalDataService] Índice Flatbush eliminado de IndexedDB');
  } catch (err) {
    console.warn('[LocalDataService] Error eliminando índice de IndexedDB:', err);
  }
}

export function injectTestData(data: Map<InfrastructureCategory, LocalFeature[]>): LoadStats {
  clearLocalData();
  const startTime = performance.now();
  const stats: LoadStats = { totalFeatures: 0, byCategory: {} as Record<InfrastructureCategory, number>, loadTimeMs: 0, municipiosIndexados: 0 };
  for (const [cat, features] of data) {
    dataStore.set(cat, features);
    stats.byCategory[cat] = features.length;
    stats.totalFeatures += features.length;
    if (features.length > 0) fastFuzzyInstances.set(cat, new FastFuzzy(features, { keys: FUZZY_OPTIONS.keys as unknown as (keyof LocalFeature)[], threshold: FUZZY_OPTIONS.threshold }));
    for (const feature of features) {
      if (!municipioIndex.has(feature.codMun)) municipioIndex.set(feature.codMun, new Map());
      const munMap = municipioIndex.get(feature.codMun)!;
      if (!munMap.has(cat)) munMap.set(cat, []);
      munMap.get(cat)!.push(feature);
    }
  }
  stats.municipiosIndexados = municipioIndex.size;
  stats.loadTimeMs = Math.round(performance.now() - startTime);
  loadStats = stats;
  isLoaded = true;
  return stats;
}

// ============================================================================
// F023 FASE 3 - BÚSQUEDA ESPACIAL CON FLATBUSH
// ============================================================================

export interface NearbyResult { feature: LocalFeature; distance: number; }

export async function findNearby(x: number, y: number, radiusMeters: number = 1000, categoria?: InfrastructureCategory): Promise<NearbyResult[]> {
  if (!isLoaded) await loadLocalData();
  if (!spatialIndex || spatialFeatures.length === 0) { console.warn('[LocalDataService] Índice espacial no disponible'); return []; }
  const minX = x - radiusMeters, minY = y - radiusMeters, maxX = x + radiusMeters, maxY = y + radiusMeters;
  const candidateIndices = spatialIndex.search(minX, minY, maxX, maxY);
  const results: NearbyResult[] = [];
  for (const idx of candidateIndices) {
    const feature = spatialFeatures[idx];
    if (!feature) continue;
    if (categoria && feature.categoria !== categoria) continue;
    const dx = feature.x - x, dy = feature.y - y;
    const distance = Math.sqrt(dx * dx + dy * dy);
    if (distance <= radiusMeters) results.push({ feature, distance });
  }
  results.sort((a, b) => a.distance - b.distance);
  return results;
}

export async function findNearest(x: number, y: number, categoria?: InfrastructureCategory): Promise<NearbyResult | null> {
  const searchRadii = [100, 500, 1000, 5000, 10000];
  for (const radius of searchRadii) {
    const results = await findNearby(x, y, radius, categoria);
    if (results.length > 0) return results[0];
  }
  return null;
}

// ============================================================================
// EXPORT DEFAULT
// ============================================================================

export default {
  loadLocalData, isDataLoaded, getLoadStats, getLastDataSource, searchLocal,
  searchHealthLocal, searchEducationLocal, searchSecurityLocal, searchMunicipalLocal, searchEnergyLocal,
  getFeaturesByMunicipio, getFeatureCountByMunicipio, listMunicipiosConDatos,
  countByType, getUniqueByType, TYPOLOGY_TO_CATEGORY,
  findNearby, findNearest,
  toGeocodingResult, geocodeWithLocalFallback, clearLocalData, clearSpatialIndexCache, injectTestData,
};
